import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Mail, 
  Phone, 
  Check, 
  X, 
  Loader2, 
  ShieldAlert, 
  UserCheck 
} from 'lucide-react';
import './AdminCustomerApproval.css'; 

const AdminCustomerApproval = () => {
  const [pendingCustomers, setPendingCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); 

  const fetchPendingCustomers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/pending-customers`, {
        headers: { 'Authorization': token }
      });
      
      setPendingCustomers(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching pending customers:", error);
      alert("Failed to load pending customer requests.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingCustomers();
  }, []);

  const handleStatusUpdate = async (customerId, status) => {
    if (!window.confirm(`Are you sure you want to ${status.toLowerCase()} this customer account?`)) {
      return;
    }

    setActionLoading(customerId);
    try {
      const token = localStorage.getItem('adminToken');

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/customer-status/${customerId}`, 
        { status },
        { headers: { 'Authorization': token } }
      );

      alert(response.data.message || `Account successfully ${status.toLowerCase()}!`);
      setPendingCustomers(prev => prev.filter(customer => customer._id !== customerId));
      setActionLoading(null);
    } catch (error) {
      console.error(`Error updating status to ${status}:`, error);
      alert(error.response?.data?.message || "Failed to update customer status.");
      setActionLoading(null);
    }
  };

  return (
    <div className="admin-approval-container">
      <div className="admin-approval-header">
        <div className="header-title-area">
          <div className="header-icon-wrap">
            <UserCheck size={24} />
          </div>
          <div>
            <h2>Employer Registration Requests</h2>
            <p>Review and verify corporate employer accounts waiting for gateway access</p>
          </div>
        </div>
        <div className="badge-count">
          {pendingCustomers.length} Pending
        </div>
      </div>

      {isLoading ? (
        <div className="admin-loading-screen">
          <Loader2 className="spinner-icon" size={40} />
          <p>Fetching registrations...</p>
        </div>
      ) : pendingCustomers.length === 0 ? (
        <div className="admin-empty-state">
          <ShieldAlert size={48} className="empty-icon" />
          <h3>All Clear!</h3>
          <p>No new employer registration requests are currently pending review.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="approval-table">
            <thead>
              <tr>
                <th>Company / Full Name</th>
                <th>Corporate Email</th>
                <th>Phone Number</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingCustomers.map((customer) => (
                <tr key={customer._id} className="fade-in-row">
                  <td>
                    <div className="cell-content">
                      <div className="cell-icon-building">
                        <Building2 size={16} />
                      </div>
                      <span className="company-name">{customer.fullName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      <Mail size={16} className="cell-icon" />
                      <a href={`mailto:${customer.email}`} className="email-link">{customer.email}</a>
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      <Phone size={16} className="cell-icon" />
                      <span className="phone-text">{customer.phone || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="actions-cell">
                    {actionLoading === customer._id ? (
                      <div className="cell-action-loading">
                        <Loader2 className="spinner" size={18} />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="action-buttons-group">
                        <button 
                          className="btn-approve"
                          onClick={() => handleStatusUpdate(customer._id, 'Approved')}
                          title="Approve Account"
                        >
                          <Check size={16} />
                          <span>Approve</span>
                        </button>
                        <button 
                          className="btn-reject"
                          onClick={() => handleStatusUpdate(customer._id, 'Rejected')}
                          title="Reject Account"
                        >
                          <X size={16} />
                          <span>Reject</span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCustomerApproval;